import { unifiedClient } from '@/lib/unified-fetch';
import { useQuery, UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import dayjs from 'dayjs';

const queryKey = ['events'];

export interface UseEventsOptions {
  startDate?: string;
  endDate?: string;
  filters?: API.EventSearchFilter[];
  sorts?: Array<{
    field: string;
    direction: 'asc' | 'desc';
  }>;
  limit?: number;
  offset?: number;
}

export const useEvents = (options: UseEventsOptions = {}, queryOptions?: Partial<UseQueryOptions>) => {
  const {
    startDate,
    endDate,
    filters = [],
    sorts = [{ field: 'startDate', direction: 'asc' }],
    limit = 1000,
    offset = 0
  } = options;

  const effectiveFilters = [...filters];

  // Add date range filters if provided
  if (startDate && endDate) {
    effectiveFilters.push(
      { field: 'endDate', op: 'gte', value: startDate },
      { field: 'startDate', op: 'lte', value: endDate }
    );
  }

  let opts = {
    queryKey: [queryKey, { startDate, endDate, filters, sorts, limit, offset }],
    queryFn: async (): Promise<API.AppEventData[]> => {
      const response = await unifiedClient.app.POST('/events/search', {
        body: {
          filters: effectiveFilters,
          sorts,
          limit,
          offset,
        }
      });

      const { data, error } = response;

      if (error) {
        throw new Error('Failed to fetch events');
      }

      return data?.items || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  };

  Object.assign(opts, queryOptions);

  return useQuery(opts);
};

// Helper function to build search filters from filter state
export const buildEventSearchFilters = (filterState: any, pageSlug: string = 'events'): API.EventSearchFilter[] => {
  const eventsFilter = filterState?.[pageSlug] || {};
  const filters: API.EventSearchFilter[] = [];

  // Handle date range with overlap logic
  const dateRangeStart = eventsFilter['dateRangeStart'] || dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
  const dateRangeEnd = eventsFilter['dateRangeEnd'] || dayjs().add(3, 'month').format('YYYY-MM-DD');
  if (dateRangeStart && dateRangeEnd) {
    filters.push({ field: 'endDate', op: 'gte', value: String(dateRangeStart) });
    filters.push({ field: 'startDate', op: 'lte', value: String(dateRangeEnd) });
  }

  // Field mapping for API compatibility
  const fieldMap: Record<string, string> = {
    campaignId: 'campaignId',
    categoryId: 'categoryId',
    source: 'source',
    itemId: 'itemId',
    itemName: 'itemName',
    name: 'name',
  };

  // Process other filters
  Object.entries(eventsFilter).forEach(([key, value]) => {
    if (key === 'dateRangeStart' || key === 'dateRangeEnd') {
      return; // Skip date range fields - already handled above
    }

    if (value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)) {
      let field: any = key;
      let op: any = 'eq';

      // Handle array values (multiple selections)
      if (Array.isArray(value) && value.length > 0) {
        op = 'in';
      } else if (key.endsWith('_gte')) {
        field = key.replace('_gte', '');
        op = 'gte';
      } else if (key.endsWith('_lte')) {
        field = key.replace('_lte', '');
        op = 'lte';
      } else if (key.endsWith('_like')) {
        field = key.replace('_like', '');
        op = 'iLike';
      } else if (key.includes('name')) {
        field = key;
        op = 'contains';
      }

      const mappedField = fieldMap[field];
      if (mappedField) {
        let filterValue: string | number | string[] | number[];
        if (Array.isArray(value)) {
          filterValue = value.map(v => String(v));
        } else {
          filterValue = String(value);
        }
        filters.push({ field: mappedField as any, op: op as any, value: filterValue });
      }
    }
  });

  return filters;
};

// Infinite scroll version that manages extended date ranges
export const useEventsInfiniteScroll = (
  baseOptions: UseEventsOptions = {}, 
  queryOptions?: Partial<UseQueryOptions>
) => {
  const queryClient = useQueryClient();
  
  // Use the base events hook
  const eventsQuery = useEvents(baseOptions, queryOptions);
  
  // Function to extend the date range and refetch
  const extendDateRange = useCallback(async (direction: 'left' | 'right', monthsToExtend: number = 1) => {
    const { filters = [] } = baseOptions;
    
    // Find existing date range filters
    let startDateFilter = filters.find(f => f.field === 'endDate' && f.op === 'gte');
    let endDateFilter = filters.find(f => f.field === 'startDate' && f.op === 'lte');
    
    let newStartDate: string;
    let newEndDate: string;
    
    if (startDateFilter && endDateFilter) {
      // Extend existing range
      newStartDate = String(startDateFilter.value);
      newEndDate = String(endDateFilter.value);
    } else {
      // Use current date as fallback
      const today = dayjs();
      newStartDate = today.subtract(1, 'month').format('YYYY-MM-DD');
      newEndDate = today.add(1, 'month').format('YYYY-MM-DD');
    }
    
    if (direction === 'left') {
      // Extend to the left (earlier dates)
      newStartDate = dayjs(newStartDate).subtract(monthsToExtend, 'month').format('YYYY-MM-DD');
    } else {
      // Extend to the right (later dates)
      newEndDate = dayjs(newEndDate).add(monthsToExtend, 'month').format('YYYY-MM-DD');
    }
    
    // Update filters with new date range
    const updatedFilters = filters.filter(f => 
      !(f.field === 'endDate' && f.op === 'gte') && 
      !(f.field === 'startDate' && f.op === 'lte')
    );
    
    updatedFilters.push(
      { field: 'endDate', op: 'gte', value: newStartDate },
      { field: 'startDate', op: 'lte', value: newEndDate }
    );
    
    const updatedOptions = {
      ...baseOptions,
      filters: updatedFilters,
    };
    
    // Invalidate and refetch with new options
    const queryKeyWithParams = [queryKey, updatedOptions];
    await queryClient.invalidateQueries({ queryKey: queryKeyWithParams });
    
    return updatedOptions;
  }, [baseOptions, queryClient]);
  
  return {
    ...eventsQuery,
    extendDateRange,
  };
};
